/**
 * Feature hints (speech bubbles pointing at a touchpoint, see sections/feature-hints.liquid).
 *
 * Each <feature-hint> waits for its target element (VisionAI app blocks render asynchronously), positions itself
 * next to it (position: fixed, recalculated on scroll/resize) and remembers dismissal in local/session storage.
 */
(() => {
  if (customElements.get("feature-hint")) {
    return;
  }

  const STORAGE_PREFIX = "feature-hint:";
  const MOBILE_QUERY = "screen and (max-width: 999px)";
  const GAP = 12;
  const VIEWPORT_PADDING = 8;
  const TARGET_WAIT_TIMEOUT = 20000;

  // Selectors are tried in order, the first visible match wins
  const TARGET_PRESETS = {
    // VisionAI wraps the toggle and disables its pointer events, so clicks only reach the wrapper
    search: [".vision-search-button", "#search-toggle"],
    pdp_cross_selling: [
      '[id^="xsvai-xsim-"][id*="visionai_cross_selling"] .xsvai-title',
      '[id^="xsvai-xsim-"][id*="visionai_cross_selling"]',
      '[id^="shopify-block-"][id*="visionai_cross_selling"]',
    ],
    pdp_similar_products: [
      '[id^="xsvai-xsim-"][id*="visionai_similar_products"] .xsvai-title',
      '[id^="xsvai-xsim-"][id*="visionai_similar_products"]',
      '[id^="shopify-block-"][id*="visionai_similar_products"]',
    ],
  };

  // Full-width headings: point at the heading text instead of the center of the (much wider) element box
  const TEXT_ANCHORED_TARGETS = ["pdp_cross_selling", "pdp_similar_products"];

  const OPPOSITE_POSITION = { top: "bottom", bottom: "top", left: "right", right: "left" };

  const readStorage = (storage, key) => {
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  };

  const writeStorage = (storage, key, value) => {
    try {
      storage.setItem(key, value);
    } catch {
      // Storage may be unavailable (private mode, blocked cookies), the hint then simply shows again
    }
  };

  const formatLocalDate = (date) => {
    const pad = (value) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };

  const isVisible = (element) => {
    if (!element || !element.isConnected) {
      return false;
    }

    const rect = element.getBoundingClientRect();

    if (rect.width === 0 && rect.height === 0) {
      return false;
    }

    return getComputedStyle(element).visibility !== "hidden";
  };

  class FeatureHint extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.target = null;
      this.isShown = false;
      this.forceShow = false;

      this.querySelector("[data-feature-hint-close]")?.addEventListener("click", () => this.dismiss("close_button"), {
        signal: this.abortController.signal,
      });

      if (this.designMode) {
        this.setupEditorEvents();
      }

      if (!this.shouldRender()) {
        return;
      }

      this.waitForTarget().then((target) => {
        if (!target || !this.isConnected) {
          return;
        }

        this.target = target;
        this.scheduleShow();
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.mutationObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      clearTimeout(this.showTimeout);
      clearTimeout(this.waitTimeout);
      clearInterval(this.waitInterval);
      cancelAnimationFrame(this.animationFrame);
    }

    /* Configuration */

    get designMode() {
      return this.hasAttribute("design-mode");
    }

    get hintId() {
      return this.getAttribute("hint-id") || this.dataset.blockId;
    }

    get storageKey() {
      return `${STORAGE_PREFIX}${this.hintId}`;
    }

    get showMode() {
      return this.getAttribute("show-mode") || "until_closed";
    }

    get preferredPosition() {
      return this.getAttribute("position") || "bottom";
    }

    get targetSelectors() {
      const target = this.getAttribute("target");

      if (target === "custom") {
        const selector = (this.getAttribute("target-selector") || "").trim();
        return selector ? [selector] : [];
      }

      return TARGET_PRESETS[target] || [];
    }

    shouldRender() {
      if (this.designMode) {
        return true;
      }

      const device = this.getAttribute("device");
      const isMobile = window.matchMedia(MOBILE_QUERY).matches;

      if ((device === "mobile" && !isMobile) || (device === "desktop" && isMobile)) {
        return false;
      }

      // Dates are already checked in Liquid, this re-check protects against cached pages
      const today = formatLocalDate(new Date());
      const startDate = this.getAttribute("start-date");
      const endDate = this.getAttribute("end-date");

      if ((startDate && today < startDate) || (endDate && today > endDate)) {
        return false;
      }

      if (this.showMode === "until_closed") {
        return readStorage(localStorage, this.storageKey) === null;
      }

      if (this.showMode === "once_per_session") {
        return readStorage(sessionStorage, this.storageKey) === null;
      }

      return true;
    }

    /* Target lookup */

    findTarget() {
      for (const selector of this.targetSelectors) {
        let elements;

        try {
          elements = document.querySelectorAll(selector);
        } catch {
          console.warn(`[feature-hint] Invalid target selector "${selector}" for hint "${this.hintId}"`);
          continue;
        }

        const visibleElement = Array.from(elements).find((element) => !this.contains(element) && isVisible(element));

        if (visibleElement) {
          return visibleElement;
        }
      }

      return null;
    }

    waitForTarget() {
      const target = this.findTarget();

      if (target) {
        return Promise.resolve(target);
      }

      if (this.targetSelectors.length === 0) {
        return Promise.resolve(null);
      }

      return new Promise((resolve) => {
        const finish = (result) => {
          this.mutationObserver?.disconnect();
          clearTimeout(this.waitTimeout);
          clearInterval(this.waitInterval);
          resolve(result);
        };

        const check = () => {
          const lateTarget = this.findTarget();

          if (lateTarget) {
            finish(lateTarget);
          }
        };

        // Catches app blocks being injected...
        this.mutationObserver = new MutationObserver(check);
        this.mutationObserver.observe(document.body, { childList: true, subtree: true });

        // ...and targets that only become visible through CSS (no DOM mutation), e.g. during header initialisation
        this.waitInterval = setInterval(check, 500);

        // If the touchpoint never shows up (app block disabled, A/B variant without it...) the hint stays hidden
        this.waitTimeout = setTimeout(() => finish(this.findTarget()), TARGET_WAIT_TIMEOUT);
      });
    }

    /* Show / hide */

    scheduleShow() {
      const delay = this.designMode ? 0 : Math.max(0, parseInt(this.getAttribute("delay") || "0", 10)) * 1000;

      const showAfterDelay = () => {
        this.showTimeout = setTimeout(() => this.show(), delay);
      };

      if (this.hasAttribute("show-on-scroll") && !this.designMode) {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this.intersectionObserver.disconnect();
              showAfterDelay();
            }
          },
          { threshold: 0.5 }
        );

        this.intersectionObserver.observe(this.target);
      } else {
        showAfterDelay();
      }
    }

    show() {
      if (!this.isConnected || this.isShown || !this.target) {
        return;
      }

      // A preferred target may have been injected after the initial lookup (e.g. the VisionAI search wrapper)
      this.target = this.findTarget() || this.target;
      this.isShown = true;
      this.classList.toggle("feature-hint--above-header", Boolean(this.target.closest(".shopify-section-group-header-group")));
      this.hidden = false;
      this.updatePosition();

      requestAnimationFrame(() => this.classList.add("is-visible"));

      if (this.showMode === "once_per_session" && !this.designMode) {
        writeStorage(sessionStorage, this.storageKey, "shown");
      }

      this.bindPositionListeners();

      if (this.hasAttribute("dismiss-on-target-click")) {
        // Capture runs before the target's own listeners, which may stop propagation (VisionAI's search wrapper does)
        this.target.addEventListener("click", () => this.dismiss("target_click"), {
          signal: this.abortController.signal,
          capture: true,
          once: true,
        });
      }

      this.pushDataLayerEvent("feature_hint_view");
    }

    hide() {
      this.isShown = false;
      this.classList.remove("is-visible");
      this.hidden = true;
      cancelAnimationFrame(this.animationFrame);
    }

    dismiss(reason) {
      if (!this.isShown) {
        return;
      }

      if (!this.designMode) {
        if (this.showMode === "until_closed") {
          writeStorage(localStorage, this.storageKey, "closed");
        } else if (this.showMode === "once_per_session") {
          writeStorage(sessionStorage, this.storageKey, "closed");
        }
      }

      this.pushDataLayerEvent("feature_hint_close", { feature_hint_close_reason: reason });
      this.hide();
      this.resizeObserver?.disconnect();
    }

    pushDataLayerEvent(event, extra = {}) {
      if (this.designMode || !Array.isArray(window.dataLayer)) {
        return;
      }

      window.dataLayer.push({
        event,
        feature_hint_id: this.hintId,
        feature_hint_target: this.getAttribute("target"),
        ...extra,
      });
    }

    /* Positioning */

    bindPositionListeners() {
      const signal = this.abortController.signal;
      const requestUpdate = () => this.requestPositionUpdate();

      window.addEventListener("scroll", requestUpdate, { passive: true, capture: true, signal });
      window.addEventListener("resize", requestUpdate, { passive: true, signal });

      this.resizeObserver = new ResizeObserver(requestUpdate);
      this.resizeObserver.observe(this.target);
      this.resizeObserver.observe(this);
    }

    // Keeps updating for a short time after the last event, so that CSS transitions (e.g. the header hiding
    // on scroll) are followed smoothly
    requestPositionUpdate() {
      this.lastUpdateRequest = performance.now();

      if (this.isUpdatingPosition) {
        return;
      }

      this.isUpdatingPosition = true;

      const loop = () => {
        this.updatePosition();

        if (this.isShown && performance.now() - this.lastUpdateRequest < 400) {
          this.animationFrame = requestAnimationFrame(loop);
        } else {
          this.isUpdatingPosition = false;
        }
      };

      this.animationFrame = requestAnimationFrame(loop);
    }

    getAnchorRect() {
      const targetRect = this.target.getBoundingClientRect();

      if (!TEXT_ANCHORED_TARGETS.includes(this.getAttribute("target")) || !this.target.matches(".xsvai-title")) {
        return targetRect;
      }

      const range = document.createRange();
      range.selectNodeContents(this.target);
      const textRect = range.getBoundingClientRect();

      return textRect.width > 0 && textRect.height > 0 ? textRect : targetRect;
    }

    updatePosition() {
      if (!this.isShown) {
        return;
      }

      // The target may have been replaced (e.g. app re-render) or hidden (e.g. breakpoint change)
      if (!isVisible(this.target)) {
        const newTarget = this.findTarget();

        if (!newTarget) {
          this.classList.add("is-target-hidden");
          return;
        }

        this.target = newTarget;
      }

      this.classList.remove("is-target-hidden");

      const targetRect = this.getAnchorRect();
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight;
      const width = this.offsetWidth;
      const height = this.offsetHeight;

      const fits = {
        top: targetRect.top - GAP - height >= VIEWPORT_PADDING,
        bottom: targetRect.bottom + GAP + height <= viewportHeight - VIEWPORT_PADDING,
        left: targetRect.left - GAP - width >= VIEWPORT_PADDING,
        right: targetRect.right + GAP + width <= viewportWidth - VIEWPORT_PADDING,
      };

      let position = this.preferredPosition;

      if (!fits[position]) {
        if (position === "left" || position === "right") {
          position = fits[OPPOSITE_POSITION[position]] ? OPPOSITE_POSITION[position] : "bottom";
        } else if (fits[OPPOSITE_POSITION[position]]) {
          position = OPPOSITE_POSITION[position];
        }
      }

      const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));
      let top;
      let left;
      let arrowOffset;

      if (position === "top" || position === "bottom") {
        const targetCenter = targetRect.left + targetRect.width / 2;

        left = clamp(targetCenter - width / 2, VIEWPORT_PADDING, viewportWidth - width - VIEWPORT_PADDING);
        top = position === "bottom" ? targetRect.bottom + GAP : targetRect.top - GAP - height;
        arrowOffset = clamp(targetCenter - left, 14, width - 14);
      } else {
        const targetCenter = targetRect.top + targetRect.height / 2;

        top = clamp(targetCenter - height / 2, VIEWPORT_PADDING, viewportHeight - height - VIEWPORT_PADDING);
        left = position === "right" ? targetRect.right + GAP : targetRect.left - GAP - width;
        arrowOffset = clamp(targetCenter - top, 14, height - 14);
      }

      this.setAttribute("placement", position);
      this.style.setProperty("--feature-hint-top", `${Math.round(top)}px`);
      this.style.setProperty("--feature-hint-left", `${Math.round(left)}px`);
      this.style.setProperty("--feature-hint-arrow-offset", `${Math.round(arrowOffset)}px`);
    }

    /* Theme editor */

    setupEditorEvents() {
      const signal = this.abortController.signal;
      const blockId = this.dataset.blockId;

      document.addEventListener(
        "shopify:block:select",
        (event) => {
          if (event.detail.blockId !== blockId) {
            return;
          }

          this.target = this.target && this.target.isConnected ? this.target : this.findTarget();

          if (!this.target) {
            console.warn(`[feature-hint] Target for hint "${this.hintId}" not found on this page`);
            return;
          }

          this.target.scrollIntoView({ block: "center", behavior: "smooth" });

          if (!this.isShown) {
            this.show();
          }

          this.requestPositionUpdate();
        },
        { signal }
      );
    }
  }

  customElements.define("feature-hint", FeatureHint);
})();
